import { Component, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';
import {
  Observable,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  startWith,
} from 'rxjs';
import { RequestUser } from './core/auth/states/auth.actions';
import { AuthState } from './core/auth/states/auth.state';
import { App } from './core/models/app.model';
import { GtmService } from './core/services/gtm.service';
import { I18nService } from './core/services/i18n.service';
import { FetchAppsRequest, ResetAppState } from './core/states/app.actions';
import { AppState } from './core/states/app.state';
import { ResponsiveState } from './core/states/responsive.state';
import { AlertService } from './ui/components/alert/alert.service';
import { GlobalAlertService } from './ui/services/global-alert.service';
import { IsCreationProcess } from './ui/states/ui.actions';
import { UIState } from './ui/states/ui.state';

@Component({
  selector: 'rk-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  @Select(AuthState.userId)
  user$: Observable<number>;

  @Select(AppState.currentApp)
  currentApp$: Observable<App>;

  @Select(ResponsiveState.showAppManagerMobileWarning)
  showAppManagerMobileWarning$: Observable<boolean>;

  @Select(UIState.isCreationProcess)
  isCreationProcess$: Observable<boolean>;

  @Select(AppState.currentAppIsNotOwnedByUser)
  currentAppIsNotOwnedByUser$: Observable<boolean>;

  @Select(AuthState.isAdminOrSuperuser)
  isAdminOrSuperuser$: Observable<boolean>;

  isMinWidth$: Observable<boolean>;

  private readonly subscription: Subscription = new Subscription();

  constructor(
    private readonly i18nService: I18nService,
    private readonly globalAlertService: GlobalAlertService,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly alertService: AlertService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly gtmService: GtmService,
    private readonly store: Store,
    private readonly library: FaIconLibrary,
  ) {
    this.alertService.setRootViewContainerRef(viewContainerRef);
    library.addIconPacks(fab, far, fas);
  }

  @Dispatch()
  getMe() {
    return new RequestUser();
  }

  @Dispatch()
  isCreationProcess(show: boolean) {
    return new IsCreationProcess(show);
  }

  @Dispatch()
  fetchUserApps() {
    return new FetchAppsRequest();
  }

  ngOnInit(): void {
    this.i18nService.init();
    this.getMe();
    this.globalAlertService.init();

    const routeData$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.route.root.firstChild.snapshot.data),
    );

    this.gtmService.registerPageChangeEvent();

    // determine if the current route is not the create route
    const isNotCreateRoute$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      startWith(null),
      map(() => !this.router.url.startsWith('/create')),
    );

    // determine whether to apply min width to the main container
    this.isMinWidth$ = combineLatest([
      this.store.select(ResponsiveState.showAppManagerMobileWarning),
      this.store.select(ResponsiveState.showAppConfigMobileWarning),
      isNotCreateRoute$,
    ]).pipe(
      map(
        ([showAppManagerMobileWarning, showAppConfigMobileWarning, isNotCreateRoute]) => {
          if (
            this.router.url.startsWith('/apps') ||
            this.router.url.startsWith('/notifications')
          ) {
            return !showAppManagerMobileWarning && isNotCreateRoute;
          } else if (
            this.router.url.startsWith('/update') ||
            this.router.url.startsWith('/assets') ||
            this.router.url.startsWith('/publishing') ||
            this.router.url.startsWith('/monetization')
          ) {
            return false;
          } else {
            return !showAppConfigMobileWarning && isNotCreateRoute;
          }
        },
      ),
    );

    // redirect if user doesn't own the current app and is not admin/superuser
    this.subscription.add(
      combineLatest([this.currentAppIsNotOwnedByUser$, this.isAdminOrSuperuser$])
        .pipe(filter(([isNotOwned, isAdmin]) => isNotOwned && !isAdmin))
        .subscribe(() => {
          this.store.dispatch(new ResetAppState());
          this.router.navigate(['/apps']);
        }),
    );

    // the route data is used to conditionally display certain components.
    // Additionally, it determines whether to activate the fetchUserApps.

    this.subscription.add(
      combineLatest([routeData$, this.user$]).subscribe(([data, user]) => {
        this.isCreationProcess(
          data['isCreationProcess'] !== undefined ? data['isCreationProcess'] : false,
        );
        if (data['fetchUserApps'] && user !== -1) {
          this.fetchUserApps();
        }
      }),
    );

    // sends tags to google tag manager
    this.subscription.add(
      this.currentApp$
        .pipe(
          filter(app => app !== null && app !== undefined),
          // avoids sending the tags twice
          distinctUntilChanged(
            (prev, curr) =>
              prev?.creationDate === curr?.creationDate &&
              prev?.planId === curr?.planId &&
              prev?.appId === curr?.appId,
          ),
        )
        .subscribe(app => {
          this.gtmService.pushToDataLayer({
            date_creation_id_app: app.creationDate,
            id_plan: app.planId,
            id_app: app.appId,
          });
        }),
    );

    // Adjusts for viewport height inconsistencies on specific mobile devices, ensuring a consistent layout across screens
    window.addEventListener('resize', this.updateViewportHeight);
    this.updateViewportHeight();
  }

  updateViewportHeight() {
    const r: HTMLStyleElement = document.querySelector(':root');
    const vh = window.innerHeight * 0.01;
    r.style.setProperty('--vh', `${vh}px`);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
